<template>
  <form class="project-mutate-theme" @submit.prevent="$emit('submit')">
    <Portal>
      <Modal :show="showThemeUpgradeWarning" class="project-mutate-theme__theme-upgrade-modal">
        <Card theme="flat" :title="$t('updateTemplateWarningTitle')">
          <template #default>
            <div v-html="$t('updateTemplateWarning')" />
          </template>
          <template #footer>
            <ButtonComponent :icon="['fal', 'times']" :label="$t('cancel')" @click="close" />
            <ButtonComponent :label="$t('upgradeAnyway')" @click="close" />
            <ButtonComponent theme="primary" :label="$t('chatWithSupport')" @click="chat"
          /></template>
        </Card>
      </Modal>
    </Portal>
    <Card class="project-mutate-theme__inputs">
      <Alert v-if="isCustomTheme" class="project-mutate-theme__custom" data-field="themes">{{
        $t('customThemeActiveMessage')
      }}</Alert>

      <template v-else>
        <div class="template-section-intro">
          <h2>{{ $t('selectTheme') }}</h2>
          <span>{{ $t('projectThemeHelper') }}</span>
        </div>
        <div class="project-mutate-theme__items">
          <ThemeCard
            v-for="theme in themesComp"
            :key="'theme_' + theme.value"
            :value="theme"
            :active="theme.value === value.theme"
            @select="value.theme = theme.value"
          />
        </div>

        <div
          v-if="orginalThemeType === 'old' || $store.getters.isSuperadmin"
          class="project-mutate-theme__load-trigger"
        >
          <ButtonComponent
            theme="secondary"
            :label="expand ? $t('showLess') : $t('seeOldThemes')"
            @click="expand = !expand"
            :icon="['fal', `${expand ? 'minus' : 'plus'}`]"
          />
        </div>
      </template>

      <FieldRenderer v-for="item in fields.default" :item="item" :value="value" />
      <CustomFields
        v-model="value.customFields"
        :schema="customFieldSchemas"
        :data="data"
        :placement="['theme', 'project:theme']"
        data-field="custom-fields"
        :validator="validator"
        validator-prefix="customFields"
        :disabledFields="$path('metadata.disabledFields.project', item)"
      >
        <template v-for="item in fields.slots" v-slot:[item.slotKey]>
          <FieldRenderer :item="item" :value="value" />
        </template>
      </CustomFields>
    </Card>
  </form>
</template>

<script>
import { mapState } from 'vuex'
import { i18n } from '@/i18n'

import Card from '@/components/Card'
import { AlertComponent as Alert } from 'vue-elder-alert'
import Upsell from '@/components/Upsell/Trigger'
import CustomFields from '@kvass/custom-fields'
import ThemeCard from '@/components/Project/ThemeCard.vue'
import FieldRenderer from '@/components/Project/StepsFields/FieldRenderer.vue'
import Bus from '@/bus'
import { ModalComponent as Modal } from 'vue-elder-modal'
import Portal from '@/components/Portal'

const meta = {
  order: 2,
  id: 'theme',
  label: i18n.t('designAndTheme'),
  icon: ['fal', 'palette'],
}

export { meta }
export default {
  props: {
    data: Object,
    value: Object,
    labels: Object,
    validator: Object,
    customFieldSchemas: { type: Array, default: () => [] },
    fields: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      expand: false,
      orginalThemeType: '',
      showThemeUpgradeWarning: false,
    }
  },
  watch: {
    'item.theme': {
      handler(newValue) {
        const storageKey = `project-has-old-theme-${this.item.id}`

        //To prevent customers to select old themes: check if original theme is old or new and save this in session storage.
        if (newValue) {
          const storageValue = sessionStorage.getItem(storageKey)
          this.orginalThemeType = storageValue
          //don`t update storage if value already set
          if (storageValue) return

          //find theme and set value hasOldTheme based on favorite tag
          const theme = this.themes.find(i => i.value === newValue)
          const saveValue = !['favorite'].includes((theme.tags || [{}])[0].id) ? 'old' : 'new'
          sessionStorage.setItem(storageKey, saveValue)
          this.orginalThemeType = saveValue
        }
      },
    },
    currentTheme: {
      handler(newValue) {
        if (this.showThemeUpgradeWarning) return
        const currentThemeIsFav = ['favorite'].includes((newValue.tags || [{}])[0].id)
        const savedThemeIsFav = ['favorite'].includes(
          (this.themes.find(i => i.value === this.item.theme).tags || [{}])[0].id,
        )

        this.showThemeUpgradeWarning = this.orginalThemeType === 'old' && currentThemeIsFav && !savedThemeIsFav
      },
    },
  },

  computed: {
    ...mapState({
      themes(state) {
        return (state.enums.Themes || [])
          .filter(({ type }) => type === 'project')
          .sort((a, b) => {
            if ((a.tags || [{}])[0].id === 'favorite') return -1
            return 0
          })
          .filter(item => {
            return (item.tags || []).find(i => i.id === 'intern') ? this.$store.getters.isSuperadmin : true
          })
      },
    }),
    ...mapState('Project', ['item']),
    ...mapState(['enums']),
    isCustomTheme() {
      if (!this.value.theme) return false
      return !this.currentTheme
    },

    themesComp() {
      if (!this.expand) {
        const lastAndCurrentTheme = [this.currentTheme.value, this.item.theme]

        return this.themes.filter(i => {
          return ['favorite'].includes((i.tags || [{}])[0].id) || lastAndCurrentTheme.includes(i.value)
        })
      }
      return this.themes
    },

    currentTheme() {
      return this.themes.find(t => t.value === this.value.theme)
    },
  },
  methods: {
    close() {
      this.showThemeUpgradeWarning = false
    },
    getPreviewUrl(item) {
      if (!this.item.url) return
      let char = this.item.url.includes('?') ? '&' : '?'
      return `${this.item.url}${char}theme=${item.value}`
    },
    chat() {
      Bus.$emit(
        'chat',
        `${this.$t('upsaleRequestTemplate', [
          this.$t('supportUpdateTemplate', { template: this.currentTheme.label, project: this.value.name }),
        ])}`,
      )
    },
  },

  components: {
    Card,
    Alert,
    Upsell,
    CustomFields,
    ThemeCard,
    FieldRenderer,
    Modal,
    Portal,
  },
}
</script>

<style lang="scss">
.project-mutate-theme {
  $cols: 2;

  &__load-trigger {
    grid-column-end: span 2;
    text-align: center;
    margin-bottom: 2rem;
  }

  &__theme-upgrade-modal {
    .card {
      max-width: 700px;
      min-width: 400px;

      &__title {
        border-bottom: 1px solid var(--border-color);
        padding-bottom: 1rem;
      }
    }
  }
  &__items {
    grid-column-end: span 2;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 2rem;
    margin-bottom: 2rem;

    .theme-card:not(.theme-card--shrink) {
      grid-column-end: span 2;
      display: grid;
      grid-template-columns: 0.7fr 1fr;
      .link-card {
        &__image {
          padding-top: 0.5rem;
          background-color: $beige;
          aspect-ratio: 1.57/1;
        }
      }
      &.theme-card--active {
        background-color: hsl(var(--secondary-h), var(--secondary-s), 92%);
      }
    }

    .theme-card--shrink.theme-card {
      .link-card {
        &__image {
          aspect-ratio: 4/2;
          border-radius: inherit;
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
          img {
            transform: translate(0, 20px);
          }
        }

        &__content {
          padding: 1rem;
          h3 {
            margin: 0;
          }
        }
      }
    }
  }

  &__inputs {
    max-width: 900px;
    margin: auto;

    .card__content {
      display: grid;
      grid-template-columns: repeat($cols, 1fr);
      grid-gap: 1rem;
      align-items: baseline;

      [data-field='site-settings'],
      [data-field='themes'] {
        grid-column-end: span 2;
      }
    }
  }

  &__preview {
    font-size: 0.8em;
  }

  .custom-fields {
    grid-column-end: span $cols;
  }
}
</style>
